import { FC, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { useStyles } from "./style";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import Loader from "router/components/Loader";
import { useDispatch } from "react-redux";
import { getCalendarData, homeSetFilter } from "actions/homeActions";
import { MonthSelect } from "components/elements/MonthSelect";
import moment from "moment";
import { useDateWeekSelector } from "hooks/useDateWeekSelector";
import { MONTH_DAYS } from "./monthDays";
import { ModalWindow } from "components/templateComponents/modalWindow";
import CalendarDetails from "./CalendarDetails";
import CalendarSmallDetails from "./CalendarSmallDetails";
import { InfoBox } from "componentsNew/elements/InfoBox";
import Alert from "@material-ui/lab/Alert";
import { setNotificationsDrawerState } from "actions/notificationsActions";
import { CalendarData } from "types/DaHome";
import { sumCalendarScheduleHours } from "utils/scheduleHelpers";

interface PropsType {}

const DaHomePage: FC<PropsType> = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { userData } = useTypeSelectors((state) => state.auth);
  const { loadingData, dataList, homeFilters } = useTypeSelectors((state) => state.daHomeState);
  const { isDrawerOpen, notificationsList } = useTypeSelectors(
    (state) => state.daNotificationsState,
  );

  const changeWeekDate = (month: string) => dispatch(homeSetFilter({ month }));
  const changeYear = (year: string) => dispatch(homeSetFilter({ year }));

  useEffect(() => {
    const params = {
      user: userData.id,
      month: Number(homeFilters?.month),
      year: Number(homeFilters?.year),
    };
    dispatch(getCalendarData(params));
  }, [dispatch, homeFilters.month, homeFilters?.year, userData.id]);

  const { month, year, dateConditionMonth, onMonthReduce, onMonthIncrease } = useDateWeekSelector({
    defaultMonth: homeFilters?.month,
    defaultYear: homeFilters?.year,
    onMonthSet: changeWeekDate,
    onYearSet: changeYear,
  });

  return (
    <Box className={classes.root}>
      <Loader isLoading={loadingData} />
      <Paper className={`${classes.paper} ${classes.daHomePaper}`}>
        <MonthSelect
          month={month}
          year={year}
          onNext={onMonthIncrease}
          onPrev={onMonthReduce}
          disabled={dateConditionMonth()}
          hasMonthName
        />
        <InfoBox
          label={"Hours this month"}
          value={`${dataList
            ?.filter((item) => item.is_available)
            ?.reduce(
              (a: number, b: CalendarData) => sumCalendarScheduleHours(a, b, dataList),
              0,
            )}h`}
        />
      </Paper>
      {notificationsList.filter((message) => !message.is_read).length ? (
        <Paper
          onClick={() => dispatch(setNotificationsDrawerState(!isDrawerOpen))}
          className={classes.paper}
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "space-between",
            flexWrap: "wrap",
            cursor: "pointer",
          }}
        >
          <Alert severity="warning" className={classes.warningColor}>
            <InfoBox
              label={"Notice"}
              value={`You have ${notificationsList.filter((message) => !message.is_read).length} unread message${notificationsList.filter((message) => !message.is_read).length > 1 ? "s" : ""}`}
            />
          </Alert>
        </Paper>
      ) : (
        ""
      )}
      <TableContainer className={classes.tableContainer}>
        <Table style={{ height: "fit-content" }} stickyHeader>
          <TableHead>
            {MONTH_DAYS?.map((col: any, index) => (
              <TableCell key={col.id} className={classes.tableCell}>
                <Typography
                  style={index < 1 ? { fontWeight: 600 } : {}}
                  className={`${classes.headText} ${classes.headDeskText}`}
                >
                  {col}
                </Typography>
                <Typography
                  style={index < 1 ? { fontWeight: 600 } : {}}
                  className={`${classes.headText} ${classes.headMobileText}`}
                >
                  {col.slice(0, 3)}
                </Typography>
              </TableCell>
            ))}
          </TableHead>
          <TableBody>
            {getMonthDatesTemplate(getAllDatesOfMonth(year, month)).map((item, index) => (
              <TableRow key={`week-${index}-${month}-${year}`}>
                <TableCell
                  style={{ fontWeight: 600, textAlign: "center" }}
                  className={`${classes.tableDateCell}`}
                >
                  {moment(item[0].date).format("w")}
                </TableCell>
                {item.map(
                  (date: { day_number: number; month: number; day: number; date: string }) => (
                    <TableCell
                      key={date.date}
                      className={`
                        ${classes.tableDateCell}
                        ${classes.leftBorderCell}
                        ${month === Number(date.month) && classes.cellHover}
                        ${month === Number(date.month) && moment().isAfter(moment(date.date).add(1, "days")) ? classes.cellDisabled : ""}
                      `}
                    >
                      <ModalWindow
                        isDisabled={month !== Number(date.month)}
                        modal_name={`calendar-schedule-${date.date}-modal`}
                        buttonType={"custom"}
                        modalTitle={`Schedule for ${date.date}`}
                        modalDescription={""}
                        customButtonElement={
                          <>
                            {month !== Number(date.month) ? (
                              <Typography></Typography>
                            ) : (
                              <Box className={classes.tableCellContent}>
                                <Typography>{date.day_number} </Typography>
                                <CalendarSmallDetails
                                  scheduleData={dataList?.find(
                                    (data) => data.schedule_date === date.date,
                                  )}
                                />
                              </Box>
                            )}
                          </>
                        }
                      >
                        <CalendarDetails
                          scheduleData={dataList?.find((data) => data.schedule_date === date.date)}
                        />
                      </ModalWindow>
                    </TableCell>
                  ),
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

function getAllDatesOfMonth(year: number, month: number) {
  const startOfMonth = moment([year, month - 1]).startOf("month");
  const endOfMonth = moment([year, month - 1]).endOf("month");

  const calendarStart = startOfMonth.clone().startOf("week");
  const calendarEnd = endOfMonth.clone().endOf("week");

  const dates = [];
  for (
    let current = calendarStart.clone();
    current.isSameOrBefore(calendarEnd);
    current.add(1, "day")
  ) {
    dates.push({
      day_number: current.date(),
      month: current.format("M"),
      day: current.format("dddd"),
      date: current.format("YYYY-MM-DD"),
    });
  }

  return dates;
}

function getMonthDatesTemplate(dates: any[]) {
  const result = [];
  for (let i = 0; i < dates.length; i += 7) {
    result.push(dates.slice(i, i + 7));
  }
  return result;
}

export default DaHomePage;
